import classNames from 'classnames';
import { FC, ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ImageField, ImageType } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { Button, ButtonTypes, customScrollStyles, MultiLineField, TextField } from '@gem/ui-kit';

import { Avatar, AvatarTypes } from 'APP/components/Avatar/Avatar';
import { minLengthValidation } from 'APP/packages/form/validation/minLengthValidation';
import { requiredValidation } from 'APP/packages/form/validation/requiredValidation';

import { IWorkspaceFormData, useEditWorkspaceFormPresenter } from './EditWorkspaceForm.presenter';
import styles from './EditWorkspaceForm.styles.m.css';

interface IEditProfileFormProps {
  submit(data: IWorkspaceFormData): Promise<void>;
}

export const EditWorkspaceForm: FC<IEditProfileFormProps> = ({ submit }) => {
  const { t } = useTranslation();
  const presenter = useEditWorkspaceFormPresenter();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = useForm<IWorkspaceFormData>({
    mode: 'onChange',
    defaultValues: presenter.defaultValues,
  });

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(submit)}
    >
      <div className={classNames(styles.formContent, customScrollStyles.container)}>
        <Controller
          control={control}
          name="avatarUrl"
          render={({ field }): ReactElement => (
            <ImageField
              className={styles.imageField}
              name={field.name}
              value={field.value}
              type={ImageType.SQUARE}
              onLoad={presenter.onImageLoad}
              renderImage={(url): ReactElement => (
                <Avatar
                  url={url}
                  type={AvatarTypes.Square_50}
                />
              )}
              emptyImage={
                <Avatar
                  type={AvatarTypes.Square_50}
                  url=""
                  title={presenter.avatarTitle}
                  backgroundColorIndex={presenter.avatarColorIndex}
                />
              }
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="name"
          rules={{
            ...requiredValidation<IWorkspaceFormData>(
              t('common_required_field_error', { '0': t('workspace_name_hint') })
            ),
            ...minLengthValidation<IWorkspaceFormData>(2, t('common_min_length_error', { '0': 2 })),
          }}
          render={({ field, fieldState }): ReactElement => (
            <TextField
              className={styles.formField}
              {...field}
              label={t('workspace_name_hint')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }): ReactElement => (
            <MultiLineField
              className={classNames(styles.formField, styles.description)}
              {...field}
              label={t('workspace_description_hint')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
            />
          )}
        />
      </div>

      <div className={styles.formActions}>
        <Button
          type={ButtonTypes.Submit}
          theme="primary"
          size="10"
          isLoading={isSubmitting}
          isDisabled={!isValid}
        >
          {t('common_save')}
        </Button>
      </div>
    </form>
  );
};
